import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { TimeInterval } from '@cotera/api';
import { atcQueryKeys } from '../query-keys';
import { SummaryCards } from '../summary-cards';

interface ServiceSummaryCardsProps {
  timeInterval: TimeInterval;
  serviceFilter?: string;
}

export const ServiceSummaryCards: React.FC<ServiceSummaryCardsProps> = ({
  timeInterval,
  serviceFilter,
}) => {
  const client = useUntenantedClient();

  const { data: statusData } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getStatusSummary({ timeInterval });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.statusSummary(timeInterval),
  });

  const { data: activeData } = useSuspenseQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getActiveInvocationsByOrg({});
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.activeInvocationsByOrg(),
  });

  const filtered =
    serviceFilter !== undefined
      ? statusData.filter((s) => s.service === serviceFilter)
      : statusData;

  const totalSuccess = filtered.reduce((sum, s) => sum + s.successCount, 0);
  const totalFailures = filtered.reduce((sum, s) => sum + s.failureCount, 0);
  const showActive = serviceFilter === undefined;
  const activeInvocations = showActive
    ? activeData.reduce((sum, a) => sum + a.activeCount, 0)
    : 0;
  const totalCompleted = totalSuccess + totalFailures;
  const totalJobs = totalCompleted + activeInvocations;
  const successRate =
    totalCompleted > 0 ? (totalSuccess / totalCompleted) * 100 : 0;

  return (
    <SummaryCards
      totalJobs={totalJobs}
      successRate={successRate}
      totalFailures={totalFailures}
      activeInvocations={showActive ? activeInvocations : undefined}
    />
  );
};
